/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Search, FilterX } from 'lucide-react';
import type { EnvironmentType, DeploymentStatus } from '../../lib/api';
import { getEnvironmentTypeLabel, getDeploymentStatusLabel } from '../../lib/api/deployments';

interface DeploymentFiltersProps {
  searchTerm: string;
  environmentFilter: EnvironmentType | 'all';
  statusFilter: DeploymentStatus | 'all';
  onSearchChange: (value: string) => void;
  onEnvironmentFilterChange: (value: EnvironmentType | 'all') => void;
  onStatusFilterChange: (value: DeploymentStatus | 'all') => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const ENVIRONMENT_TYPES: EnvironmentType[] = ['dev', 'test', 'staging', 'production', 'demo'];
const STATUS_OPTIONS: DeploymentStatus[] = ['running', 'stopped', 'deploying', 'failed', 'unhealthy', 'pending'];

export function DeploymentFilters({
  searchTerm,
  environmentFilter,
  statusFilter,
  onSearchChange,
  onEnvironmentFilterChange,
  onStatusFilterChange,
  onClearFilters,
  hasActiveFilters,
}: DeploymentFiltersProps) {
  return (
    <div style={{ 
      display: 'flex', 
      gap: '12px', 
      marginBottom: '24px', 
      flexWrap: 'wrap',
      alignItems: 'center'
    }}>
      {/* Search */}
      <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
        <Search 
          size={16} 
          style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }} 
        />
        <input
          type="text"
          placeholder="Search deployments..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px 10px 40px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s',
          }}
          onFocus={(e) => e.target.style.borderColor = '#0066cc'}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
        />
      </div>

      {/* Environment Filter */}
      <select
        value={environmentFilter}
        onChange={(e) => onEnvironmentFilterChange(e.target.value as EnvironmentType | 'all')}
        style={{
          padding: '10px 32px 10px 12px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          fontSize: '14px',
          backgroundColor: 'white',
          cursor: 'pointer',
          outline: 'none',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 10px center',
        }}
      >
        <option value="all">All Environments</option>
        {ENVIRONMENT_TYPES.map((type) => (
          <option key={type} value={type}>
            {getEnvironmentTypeLabel(type)}
          </option>
        ))}
      </select>

      {/* Status Filter */}
      <select
        value={statusFilter}
        onChange={(e) => onStatusFilterChange(e.target.value as DeploymentStatus | 'all')}
        style={{
          padding: '10px 32px 10px 12px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          fontSize: '14px',
          backgroundColor: 'white',
          cursor: 'pointer',
          outline: 'none',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 10px center',
        }}
      >
        <option value="all">All Statuses</option>
        {STATUS_OPTIONS.map((status) => (
          <option key={status} value={status}>
            {getDeploymentStatusLabel(status)}
          </option>
        ))}
      </select>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '10px 16px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: 'white',
            color: '#6b7280',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          <FilterX size={16} />
          Clear
        </button>
      )}
    </div>
  );
}

